const createAbortError = () => {
  const error = new Error('Request was cancelled')
  error.name = 'AbortError'
  return error
}

export const throwIfAborted = signal => {
  if (signal && signal.aborted) throw createAbortError()
}

// Count callers before starting/deduplicating work. Each caller can leave
// independently; only the last one aborts the underlying operation.
export const getSharedRequest = (requests, key, start, signal) => {
  if (signal && signal.aborted) return Promise.reject(createAbortError())

  let request = requests.get(key)
  if (!request) {
    request = {
      controller: new AbortController(),
      consumers: 0,
      settled: false,
      promise: null
    }
    requests.set(key, request)
    const finish = () => {
      request.settled = true
      if (requests.get(key) === request) requests.delete(key)
    }
    request.promise = Promise.resolve()
      .then(() => {
        throwIfAborted(request.controller.signal)
        return start(request.controller.signal)
      })
      .then(
        value => {
          finish()
          return value
        },
        error => {
          finish()
          throw error
        }
      )
  }
  request.consumers += 1

  return new Promise((resolve, reject) => {
    let released = false
    const release = () => {
      if (released) return
      released = true
      if (signal) signal.removeEventListener('abort', onAbort)
      request.consumers -= 1
      if (request.consumers === 0 && !request.settled) {
        // Remove synchronously so a new caller cannot join cancelled work.
        if (requests.get(key) === request) requests.delete(key)
        request.controller.abort()
      }
    }
    const onAbort = () => {
      release()
      reject(createAbortError())
    }
    if (signal) signal.addEventListener('abort', onAbort, { once: true })
    request.promise.then(
      value => {
        if (released) return
        release()
        resolve(value)
      },
      error => {
        if (released) return
        release()
        reject(error)
      }
    )
  })
}
